"use client"

import React, {useMemo, useState} from "react";
import {useCompanyId} from "@/hooks/use-company-id";
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from "@/components/ui/dialog";
import {Button} from "@/components/ui/button";
import {ResizableTextarea} from "@/app/components/ResizableTextarea";
import {
  getDefaultProjectTemplateId,
  useProjectCreation,
  useProjectTemplates
} from "@/app/components/home/useProjectHooks";
import {projectKindBug, projectKindIdea, projectKindQuickWin} from "@/app/util/projectsUtil";
import {ProjectTemplate} from "@/pb/api/devplan/types/templates";
import {cn} from "@/lib/utils";
import {Bug, FileText, Layout, LifeBuoy, MessagesSquare, Sparkles, Zap} from "lucide-react";
import {ScopingIntakeDialog} from "@/app/components/scoping/ScopingIntakeDialog";
import {useBoundStore} from "@/hooks/queries/stores/use-bound-store";
import {useTemplates} from "@/hooks/queries/use-templates";
import {ProjectEntity} from "@/pb/api/devplan/types/documents";
import {createDiscoveryDocument} from "@/app/components/ideas/use-idea-discovery";
import {bugPath, documentPath, projectPath, quickWinPath, roadmapPath} from "@/app/paths";
import {setReturnToHomeNew} from "@/app/hooks/useProjectNavigation";
import {useRouter} from "next/navigation";
import {toast} from "sonner";
import {
  AttachmentsAndIssueLinksInput,
  CreationAttachmentsPayload,
} from "@/app/components/projects/creation/AttachmentsAndIssueLinksInput";
import {useCompanyFeatureFlag} from "@/hooks/queries/use-feature-flags";
import {Input} from "@/components/ui/input";
import {prototypeWalkthroughAPI} from "@/app/apiPaths";
import {jr} from "@/hooks/queries/response-handler";
import {inputValueToDate} from "@/app/components/projects/dateInput";
import {
  OptimisticFilePreviewList,
  useOptimisticAttachments,
} from "@/app/components/projects/creation/OptimisticAttachmentsInput";
import {LinkPreviewList} from "@/app/components/editor/link-preview/LinkPreviewList";
import {FileDropOverlay} from "@/app/components/FileDropOverlay";

export type QuickActionKey = "brainstorm" | "quickWin" | "bug" | "project" | "prototype" | "projectIntake" | "scopeMulti";

interface QuickActionDef {
  key: QuickActionKey;
  Icon: React.ComponentType<{className?: string}>;
  iconClassName: string;
  title: string;
  description: string;
  requiresIdeas?: boolean;
  requiresPrototype?: boolean;
  requiresBugs?: boolean;
  requiresCreateProjects?: boolean;
}

export const quickActionDefs: QuickActionDef[] = [
  {
    key: "brainstorm",
    Icon: Sparkles,
    iconClassName: "h-5 w-5 text-sky-500",
    title: "Brainstorm a new project",
    description: "Start from an idea and explore possibilities.",
    requiresIdeas: true,
  },
  {
    key: "quickWin",
    Icon: Zap,
    iconClassName: "h-5 w-5 text-violet-500",
    title: "Build a small feature",
    description: "Quick wins and rapid implementations.",
  },
  {
    key: "bug",
    Icon: Bug,
    iconClassName: "h-5 w-5 text-red-500",
    title: "Report a bug",
    description: "Triage and plan a fix for a known issue.",
    requiresBugs: true,
  },
  {
    key: "project",
    Icon: FileText,
    iconClassName: "h-5 w-5 text-emerald-500",
    title: "Start a new project",
    description: "Create a project and go straight to planning.",
  },
  {
    key: "prototype",
    Icon: Layout,
    iconClassName: "h-5 w-5 text-amber-500",
    title: "Start from Prototype",
    description: "Analyze a prototype URL and create a project from it.",
    requiresPrototype: true,
  },
  {
    key: "projectIntake",
    Icon: LifeBuoy,
    iconClassName: "h-5 w-5 text-amber-600",
    title: "Create Projects",
    description: "Bring in customer context — files, issue links, docs — and we'll process it in the background.",
    requiresCreateProjects: true,
  },
  {
    key: "scopeMulti",
    Icon: MessagesSquare,
    iconClassName: "h-5 w-5 text-indigo-500",
    title: "Scope/track work",
    description: "Drop a brief and chat with the agent. It'll propose several projects you can refine and create together.",
    requiresCreateProjects: true,
  },
];

interface HomeQuickActionsProps {
  /**
   * Optional callback invoked after a project/idea/quick win
   * has been successfully created.
   */
  onActionCompleted?: () => void;
}

export function HomeQuickActions({onActionCompleted}: HomeQuickActionsProps) {
  const [brainstormOpen, setBrainstormOpen] = useState(false);
  const [quickWinOpen, setQuickWinOpen] = useState(false);
  const [bugOpen, setBugOpen] = useState(false);
  const [specOpen, setSpecOpen] = useState(false);
  const [prototypeOpen, setPrototypeOpen] = useState(false);
  const [projectIntakeOpen, setProjectIntakeOpen] = useState(false);
  const [scopeMultiOpen, setScopeMultiOpen] = useState(false);
  const {companyId: currentCompanyId} = useCompanyId();
  const bugsEnabled = useCompanyFeatureFlag(ff => ff.wsBugs)
  const createProjectsEnabled = useCompanyFeatureFlag(ff => ff.wsCreateProjects)

  const visibleDefs = quickActionDefs.filter(def =>
    (!def.requiresBugs || bugsEnabled) &&
    (!def.requiresCreateProjects || createProjectsEnabled)
  );
  const gridCols = visibleDefs.length >= 4
    ? "md:grid-cols-2 lg:grid-cols-4"
    : "md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]";

  return (
    <div className="mt-8 space-y-4">
      <div className={cn("grid gap-4", gridCols)}>
        {visibleDefs.map(({key, Icon, iconClassName, title, description}) => {
            const onClick =
              key === "brainstorm" ? () => setBrainstormOpen(true)
              : key === "quickWin" ? () => setQuickWinOpen(true)
              : key === "bug" ? () => setBugOpen(true)
              : key === "prototype" ? () => setPrototypeOpen(true)
              : key === "projectIntake" ? () => setProjectIntakeOpen(true)
              : key === "scopeMulti" ? () => setScopeMultiOpen(true)
              : () => setSpecOpen(true);
            return (
              <QuickActionCard
                key={key}
                icon={<Icon className={iconClassName}/>}
                title={title}
                description={description}
                onClick={onClick}
              />
            );
          })}
      </div>

      <BrainstormIdeaDialog
        open={brainstormOpen}
        onOpenChange={setBrainstormOpen}
        onCompleted={onActionCompleted}
      />
      <QuickWinDialog
        open={quickWinOpen}
        onOpenChange={setQuickWinOpen}
        onCompleted={onActionCompleted}
      />
      <BugDialog
        open={bugOpen}
        onOpenChange={setBugOpen}
        onCompleted={onActionCompleted}
      />
      <StartNewProjectDialog
        open={specOpen}
        onOpenChange={setSpecOpen}
        onCompleted={onActionCompleted}
      />
      <PrototypeWalkthroughDialog
        open={prototypeOpen}
        onOpenChange={setPrototypeOpen}
      />
      <ProjectIntakeDialog
        open={projectIntakeOpen}
        onOpenChange={setProjectIntakeOpen}
        onCompleted={onActionCompleted}
      />
      <ScopingIntakeDialog
        open={scopeMultiOpen}
        onOpenChange={setScopeMultiOpen}
        companyId={currentCompanyId}
      />
    </div>
  );
}

export interface QuickActionCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  onClick: () => void;
}

export function QuickActionCard({icon, title, description, onClick}: QuickActionCardProps) {
  return (
    <Button
      type="button"
      variant="ghost"
      onClick={onClick}
      className={cn(
        "group h-full min-w-0 flex flex-col items-start justify-start gap-2 rounded-xl border bg-card p-4 text-left shadow-sm whitespace-normal cursor-pointer",
        "transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
      )}
    >
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted">
        {icon}
      </div>
      <div className="min-w-0 flex-1 space-y-1">
        <div className="text-sm font-semibold text-foreground break-words">{title}</div>
        <div className="text-xs text-muted-foreground break-words">{description}</div>
      </div>
    </Button>
  );
}

interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCompleted?: () => void;
}

const emptyAttachmentsPayload: CreationAttachmentsPayload = {
  imageUrls: [],
  attachmentIds: [],
  issueAttachments: [],
};

// ─── Shared dialog shell ──────────────────────────────────────────────────────

interface CreationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Header
  icon: React.ReactNode;
  iconClassName: string;
  title: string;
  subtitle: string;
  dialogDescription: string;
  // Body
  inputLabel: string;
  inputPlaceholder: string;
  companyId: number;
  value: string;
  onValueChange: (v: string) => void;
  attachmentsPayload: CreationAttachmentsPayload;
  onAttachmentsChange: (v: CreationAttachmentsPayload) => void;
  attachmentInputKey: number;
  creating: boolean;
  /** Slot rendered between the attachments composer and the infoBox. */
  extraBody?: React.ReactNode;
  infoBox?: React.ReactNode;
  error?: unknown;
  // Footer
  onSubmit: () => void;
  submitDisabled: boolean;
  submitLabel: string;
  submitClassName: string;
  /** If true, footer is space-between (Cancel on left, Submit on right). Default: end-aligned. */
  footerSpread?: boolean;
}

function CreationDialog({
                          open, onOpenChange,
                          icon, iconClassName, title, subtitle, dialogDescription,
                          inputLabel, inputPlaceholder, companyId,
                          value, onValueChange,
                          attachmentsPayload, onAttachmentsChange, attachmentInputKey,
                          creating, extraBody, infoBox, error,
                          onSubmit, submitDisabled, submitLabel, submitClassName,
                          footerSpread = false,
                        }: CreationDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="space-y-3 shrink-0">
          <div className="flex items-center gap-3">
            <div className={cn("flex h-9 w-9 items-center justify-center rounded-lg", iconClassName)}>
              {icon}
            </div>
            <div className="space-y-1">
              <DialogTitle>{title}</DialogTitle>
              <p className="text-xs text-muted-foreground">{subtitle}</p>
            </div>
          </div>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto mt-4">
          <div className="space-y-4">
            {!!error && <div className="mb-2 text-sm text-destructive">{String(error)}</div>}
            <AttachmentsAndIssueLinksInput
              key={attachmentInputKey}
              companyId={companyId}
              description={value}
              onChange={onAttachmentsChange}
              disabled={creating}
              hideUploadTrigger
            >
              {({handlePaste, uploadTrigger}) => (
                <div className="space-y-2">
                  <p className="text-sm font-medium">{inputLabel}</p>
                  <div className="relative">
                    <ResizableTextarea
                      rows={4}
                      value={value}
                      onChange={e => onValueChange(e.target.value)}
                      onPaste={handlePaste}
                      placeholder={inputPlaceholder}
                      className="pr-10 pb-10"
                    />
                    <div className="absolute bottom-2 right-2">{uploadTrigger}</div>
                  </div>
                </div>
              )}
            </AttachmentsAndIssueLinksInput>
            {extraBody}
            {infoBox}
          </div>
        </div>
        <div className={cn(
          "mt-4 flex gap-3 shrink-0",
          footerSpread
            ? "flex-col-reverse sm:flex-row sm:items-center sm:justify-between"
            : "justify-end"
        )}>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onSubmit}
            disabled={submitDisabled}
            className={submitClassName}
          >
            {submitLabel}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Individual dialogs ───────────────────────────────────────────────────────

export function BrainstormIdeaDialog({open, onOpenChange, onCompleted}: ModalProps) {
  const {companyId} = useCompanyId();
  const router = useRouter();
  const {projectTemplates} = useProjectTemplates();
  const {createEmptyDocument} = useBoundStore();
  const {templates} = useTemplates();
  const [description, setDescription] = useState("");
  const [attachmentsPayload, setAttachmentsPayload] = useState<CreationAttachmentsPayload>(emptyAttachmentsPayload);
  const [attachmentInputKey, setAttachmentInputKey] = useState(0);

  const defaultTemplateId: string | undefined = useMemo(
    () => getDefaultProjectTemplateId(projectTemplates as ProjectTemplate[]),
    [projectTemplates]
  );

  // redirectPath: () => "" suppresses useProjectCreation's auto-navigation so we can route
  // manually after discovery-doc creation.
  const {createProject, creating} = useProjectCreation({
    templates: projectTemplates as ProjectTemplate[],
    templateId: defaultTemplateId,
    redirectPath: () => "",
  });

  const canSubmit = !!description.trim() && !!defaultTemplateId;
  const companyIdNum = companyId != null ? Number(companyId) : 0;

  const handleOpenChange = (next: boolean) => {
    if (!next) setAttachmentInputKey(k => k + 1);
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await createProject({
      description: description.trim(),
      imageUrls: attachmentsPayload.imageUrls,
      attachmentIds: attachmentsPayload.attachmentIds,
      issueAttachments: attachmentsPayload.issueAttachments,
      kind: projectKindIdea,
      onSuccess: async (projectId) => {
        try {
          const project = {id: projectId} as ProjectEntity;
          const docId = await createDiscoveryDocument(
            companyId,
            project,
            templates,
            createEmptyDocument
          );
          setDescription("");
          setAttachmentsPayload(emptyAttachmentsPayload);
          setAttachmentInputKey(k => k + 1);
          onOpenChange(false);
          setReturnToHomeNew();
          onCompleted?.();
          router.push(documentPath(companyId, docId));
        } catch (e) {
          toast.error(`Failed to start discovery: ${e instanceof Error ? e.message : String(e)}`);
        }
      },
    });
  };

  return (
    <CreationDialog
      open={open}
      onOpenChange={handleOpenChange}
      icon={<Sparkles className="h-5 w-5"/>}
      iconClassName="bg-sky-500/10 text-sky-500 dark:bg-sky-500/20"
      title="Brainstorm a new project"
      subtitle="Start from an idea and explore possibilities."
      dialogDescription="Describe your project idea in a few sentences. We'll help you explore the idea, generate user stories, and create a comprehensive project plan."
      inputLabel="What's your idea?"
      inputPlaceholder="Describe your project idea in a few sentences..."
      companyId={companyIdNum}
      value={description}
      onValueChange={setDescription}
      attachmentsPayload={attachmentsPayload}
      onAttachmentsChange={setAttachmentsPayload}
      attachmentInputKey={attachmentInputKey}
      creating={creating}
      infoBox={
        <div
          className="rounded-lg border border-sky-500/40 bg-sky-500/5 px-3 py-3 text-xs text-muted-foreground dark:border-sky-500/60 dark:bg-sky-500/10">
          <span className="font-semibold text-sky-600 dark:text-sky-400">Tip:</span>{" "}
          Our AI will help you explore your idea, generate user stories, and create a comprehensive project plan.
        </div>
      }
      onSubmit={handleSubmit}
      submitDisabled={!canSubmit || creating}
      submitLabel={creating ? "Starting..." : "Start brainstorming"}
      submitClassName="bg-sky-500 text-white hover:bg-sky-500/90 dark:bg-sky-400 dark:hover:bg-sky-400/90"
      footerSpread
    />
  );
}

export function QuickWinDialog({open, onOpenChange, onCompleted}: ModalProps) {
  const {companyId} = useCompanyId();
  const {projectTemplates, error} = useProjectTemplates();
  const [description, setDescription] = useState("");
  const [attachmentsPayload, setAttachmentsPayload] = useState<CreationAttachmentsPayload>(emptyAttachmentsPayload);
  const [attachmentInputKey, setAttachmentInputKey] = useState(0);

  const defaultTemplateId: string | undefined = React.useMemo(() => {
    if (!projectTemplates || projectTemplates.length === 0) return undefined;
    return (projectTemplates as ProjectTemplate[])[0].id;
  }, [projectTemplates]);

  const {createProject, creating} = useProjectCreation({
    templates: projectTemplates as ProjectTemplate[],
    templateId: defaultTemplateId,
    redirectPath: (projectId) => quickWinPath(companyId, projectId),
  });

  const companyIdNum = companyId != null ? Number(companyId) : 0;

  const handleOpenChange = (next: boolean) => {
    if (!next) setAttachmentInputKey(k => k + 1);
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    if (!description.trim()) return;
    await createProject({
      description: description.trim(),
      imageUrls: attachmentsPayload.imageUrls,
      attachmentIds: attachmentsPayload.attachmentIds,
      issueAttachments: attachmentsPayload.issueAttachments,
      kind: projectKindQuickWin,
      onSuccess: () => {
        setReturnToHomeNew();
        setDescription("");
        setAttachmentsPayload(emptyAttachmentsPayload);
        setAttachmentInputKey(k => k + 1);
        onOpenChange(false);
        onCompleted?.();
      },
    });
  };

  return (
    <CreationDialog
      open={open}
      onOpenChange={handleOpenChange}
      icon={<Zap className="h-5 w-5"/>}
      iconClassName="bg-violet-500/10 text-violet-500 dark:bg-violet-500/20"
      title="Build a small feature"
      subtitle="Quick wins and rapid implementations."
      dialogDescription="Describe the small feature or quick win you want to implement. Perfect for bug fixes, minor improvements, or features that can be completed quickly without extensive planning."
      inputLabel="What do you want to build?"
      inputPlaceholder="Describe the small feature or quick win you want to implement..."
      companyId={companyIdNum}
      value={description}
      onValueChange={setDescription}
      attachmentsPayload={attachmentsPayload}
      onAttachmentsChange={setAttachmentsPayload}
      attachmentInputKey={attachmentInputKey}
      creating={creating}
      error={error}
      infoBox={
        <div
          className="rounded-lg border border-violet-200/80 bg-violet-50 px-3 py-3 text-xs text-violet-900 dark:border-violet-500/40 dark:bg-violet-500/10 dark:text-violet-100">
          <span className="font-semibold">Perfect for:</span>{" "}
          Bug fixes, minor improvements, or features that can be completed quickly without extensive planning.
        </div>
      }
      onSubmit={handleSubmit}
      submitDisabled={creating || !description.trim() || !!error || !defaultTemplateId}
      submitLabel={creating ? "Creating…" : "Create feature"}
      submitClassName="bg-violet-500 text-white hover:bg-violet-500/90 dark:bg-violet-400 dark:hover:bg-violet-400/90"
    />
  );
}

export function BugDialog({open, onOpenChange, onCompleted}: ModalProps) {
  const {companyId} = useCompanyId();
  const {projectTemplates, error} = useProjectTemplates();
  const [description, setDescription] = useState("");
  const [attachmentsPayload, setAttachmentsPayload] = useState<CreationAttachmentsPayload>(emptyAttachmentsPayload);
  const [attachmentInputKey, setAttachmentInputKey] = useState(0);

  const defaultTemplateId: string | undefined = React.useMemo(() => {
    if (!projectTemplates || projectTemplates.length === 0) return undefined;
    return (projectTemplates as ProjectTemplate[])[0].id;
  }, [projectTemplates]);

  const {createProject, creating} = useProjectCreation({
    templates: projectTemplates as ProjectTemplate[],
    templateId: defaultTemplateId,
    redirectPath: (projectId) => bugPath(companyId, projectId),
  });

  const companyIdNum = companyId != null ? Number(companyId) : 0;

  const handleOpenChange = (next: boolean) => {
    if (!next) setAttachmentInputKey(k => k + 1);
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    if (!description.trim()) return;
    await createProject({
      description: description.trim(),
      imageUrls: attachmentsPayload.imageUrls,
      attachmentIds: attachmentsPayload.attachmentIds,
      issueAttachments: attachmentsPayload.issueAttachments,
      kind: projectKindBug,
      onSuccess: () => {
        setReturnToHomeNew();
        setDescription("");
        setAttachmentsPayload(emptyAttachmentsPayload);
        setAttachmentInputKey(k => k + 1);
        onOpenChange(false);
        onCompleted?.();
      },
    });
  };

  return (
    <CreationDialog
      open={open}
      onOpenChange={handleOpenChange}
      icon={<Bug className="h-5 w-5"/>}
      iconClassName="bg-red-500/10 text-red-500 dark:bg-red-500/20"
      title="Report a bug"
      subtitle="Triage and plan a fix for a known issue."
      dialogDescription="Describe the bug — what you expected, what actually happened, and any repro steps or screenshots. We'll draft a triage spec with requirements, a user story, and priority score."
      inputLabel="Describe the bug"
      inputPlaceholder="What's broken? Expected vs. actual, steps to reproduce, any screenshots..."
      companyId={companyIdNum}
      value={description}
      onValueChange={setDescription}
      attachmentsPayload={attachmentsPayload}
      onAttachmentsChange={setAttachmentsPayload}
      attachmentInputKey={attachmentInputKey}
      creating={creating}
      error={error}
      infoBox={
        <div
          className="rounded-lg border border-red-200/80 bg-red-50 px-3 py-3 text-xs text-red-900 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-100">
          <span className="font-semibold">Tip:</span>{" "}
          Include repro steps and screenshots when possible — the triage agent will use them to scope the fix.
        </div>
      }
      onSubmit={handleSubmit}
      submitDisabled={creating || !description.trim() || !!error || !defaultTemplateId}
      submitLabel={creating ? "Creating…" : "Report bug"}
      submitClassName="bg-red-500 text-white hover:bg-red-500/90 dark:bg-red-400 dark:hover:bg-red-400/90"
    />
  );
}

export function StartNewProjectDialog({open, onOpenChange, onCompleted}: ModalProps) {
  const {companyId} = useCompanyId();
  const router = useRouter();
  const {projectTemplates, error} = useProjectTemplates();
  const [description, setDescription] = useState("");
  const [attachmentsPayload, setAttachmentsPayload] = useState<CreationAttachmentsPayload>(emptyAttachmentsPayload);
  const [attachmentInputKey, setAttachmentInputKey] = useState(0);

  const defaultTemplateId: string | undefined = useMemo(
    () => getDefaultProjectTemplateId(projectTemplates as ProjectTemplate[]),
    [projectTemplates]
  );

  const {createProject, creating} = useProjectCreation({
    templates: projectTemplates as ProjectTemplate[],
    templateId: defaultTemplateId,
    redirectPath: () => "",
  });

  const canSubmit = !!description.trim() && !!defaultTemplateId;
  const companyIdNum = companyId != null ? Number(companyId) : 0;

  const handleOpenChange = (next: boolean) => {
    if (!next) setAttachmentInputKey(k => k + 1);
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    await createProject({
      description: description.trim(),
      imageUrls: attachmentsPayload.imageUrls,
      attachmentIds: attachmentsPayload.attachmentIds,
      issueAttachments: attachmentsPayload.issueAttachments,
      kind: undefined,
      onSuccess: (projectId) => {
        setDescription("");
        setAttachmentsPayload(emptyAttachmentsPayload);
        setAttachmentInputKey(k => k + 1);
        onOpenChange(false);
        setReturnToHomeNew();
        onCompleted?.();
        router.push(projectPath(companyId, projectId));
      },
    });
  };

  return (
    <CreationDialog
      open={open}
      onOpenChange={handleOpenChange}
      icon={<FileText className="h-5 w-5"/>}
      iconClassName="bg-emerald-500/10 text-emerald-500 dark:bg-emerald-500/20"
      title="Start a new project"
      subtitle="Create a project and go straight to planning."
      dialogDescription="Describe your project in a few sentences. We'll create a project and you can start planning right away without the discovery step."
      inputLabel="What's your project?"
      inputPlaceholder="Describe your project in a few sentences..."
      companyId={companyIdNum}
      value={description}
      onValueChange={setDescription}
      attachmentsPayload={attachmentsPayload}
      onAttachmentsChange={setAttachmentsPayload}
      attachmentInputKey={attachmentInputKey}
      creating={creating}
      error={error}
      infoBox={
        <div
          className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 px-3 py-3 text-xs text-muted-foreground dark:border-emerald-500/60 dark:bg-emerald-500/10">
          <span className="font-semibold text-emerald-600 dark:text-emerald-400">Tip:</span>{" "}
          You&apos;ll land on the project page and can add requirements, user stories, and tasks from there.
        </div>
      }
      onSubmit={handleSubmit}
      submitDisabled={!canSubmit || creating}
      submitLabel={creating ? "Creating..." : "Create project"}
      submitClassName="bg-emerald-500 text-white hover:bg-emerald-500/90 dark:bg-emerald-400 dark:hover:bg-emerald-400/90"
      footerSpread
    />
  );
}

/**
 * Project Intake Dialog ("Create Projects" quick action).
 *
 * A sibling of `StartNewProjectDialog` that adds an optional target date and
 * is the entry point for the background-processing flow: on submit, the project
 * is created and a pending job fans out per-attachment ingestion in the
 * background. The user lands on the all-projects roadmap with the new row
 * briefly highlighted; processed artifacts become visible on the project as
 * they complete, with per-item retry surfaced on the attachments panel.
 *
 * Intended to grow into a bulk / multi-project import flow; the component is
 * named generically so it can host that future toggle without another rename.
 */
export function ProjectIntakeDialog({open, onOpenChange, onCompleted}: ModalProps) {
  const {companyId} = useCompanyId();
  const router = useRouter();
  const {projectTemplates, error} = useProjectTemplates();
  const [description, setDescription] = useState("");
  const [targetDateInput, setTargetDateInput] = useState("");
  const [composerKey, setComposerKey] = useState(0);

  const defaultTemplateId: string | undefined = useMemo(
    () => getDefaultProjectTemplateId(projectTemplates as ProjectTemplate[]),
    [projectTemplates]
  );

  const {createProject, creating} = useProjectCreation({
    templates: projectTemplates as ProjectTemplate[],
    templateId: defaultTemplateId,
    redirectPath: () => "",
  });

  const companyIdNum = companyId != null ? Number(companyId) : 0;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setComposerKey(k => k + 1);
      setTargetDateInput("");
      setDescription("");
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] flex flex-col overflow-hidden">
        <DialogHeader className="space-y-3 shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600 dark:bg-amber-500/20 dark:text-amber-300">
              <LifeBuoy className="h-5 w-5"/>
            </div>
            <div className="space-y-1">
              <DialogTitle>Create Projects</DialogTitle>
              <p className="text-xs text-muted-foreground">Bring in customer context and we&apos;ll process it in the background.</p>
            </div>
          </div>
          <DialogDescription>
            Describe what&apos;s coming in. Paste Linear, Jira, or doc links and drop supporting files — we&apos;ll process them in the background while you start working.
          </DialogDescription>
        </DialogHeader>
        <ProjectIntakeBody
          key={composerKey}
          companyId={companyIdNum}
          description={description}
          onDescriptionChange={setDescription}
          targetDateInput={targetDateInput}
          onTargetDateChange={setTargetDateInput}
          creating={creating}
          error={error}
          onCancel={() => handleOpenChange(false)}
          onSubmit={async (payload) => {
            const targetDate = inputValueToDate(targetDateInput || undefined);
            await createProject({
              description: description.trim(),
              imageUrls: payload.imageUrls,
              attachmentIds: payload.attachmentIds,
              issueAttachments: payload.issueAttachments,
              kind: undefined,
              ...(targetDate ? {targetDate} : {}),
              onSuccess: (projectId) => {
                setDescription("");
                setTargetDateInput("");
                setComposerKey(k => k + 1);
                onOpenChange(false);
                onCompleted?.();
                // Project-intake flow lands the user on the all-projects
                // roadmap rather than the new project's page, so the user
                // can see the newly-added project in context while its
                // attached artifacts process in the background. The
                // highlight param triggers a brief blink on the new row
                // so it isn't missed in a long roadmap.
                router.push(`${roadmapPath(companyId)}?highlight=${projectId}`);
              },
            });
          }}
        />
      </DialogContent>
    </Dialog>
  );
}

/**
 * Inner body of the project-intake dialog. Wrapped in its own component so
 * remounting (via `composerKey`) cleanly resets the optimistic-attachments
 * hook state when the dialog closes.
 */
function ProjectIntakeBody({
  companyId,
  description,
  onDescriptionChange,
  targetDateInput,
  onTargetDateChange,
  creating,
  error,
  onCancel,
  onSubmit,
}: {
  companyId: number;
  description: string;
  onDescriptionChange: (v: string) => void;
  targetDateInput: string;
  onTargetDateChange: (v: string) => void;
  creating: boolean;
  error?: unknown;
  onCancel: () => void;
  onSubmit: (payload: CreationAttachmentsPayload) => Promise<void>;
}) {
  const optimistic = useOptimisticAttachments({
    companyId,
    description,
    disabled: creating,
  });
  const canSubmit = !!description.trim() && !optimistic.uploadsInFlight;
  const submitLabel = creating
    ? "Creating…"
    : optimistic.uploadsInFlight
      ? "Waiting for uploads…"
      : "Add project";

  return (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto mt-4">
        <div className="space-y-4 relative" {...optimistic.dragHandlers}>
          <FileDropOverlay visible={optimistic.dragActive} className="rounded-lg"/>
          {!!error && <div className="mb-2 text-sm text-destructive">{String(error)}</div>}
          <div className="space-y-2">
            <p className="text-sm font-medium">What&apos;s the request?</p>
            <div className="relative">
              <ResizableTextarea
                rows={4}
                value={description}
                onChange={e => onDescriptionChange(e.target.value)}
                onPaste={optimistic.handlePaste}
                placeholder="Summarize the customer's ask, with any context you've gathered…"
                className="pr-10 pb-10"
                disabled={creating}
              />
              <div className="absolute bottom-2 right-2">{optimistic.uploadTrigger}</div>
            </div>
          </div>
          <OptimisticFilePreviewList
            pendingFiles={optimistic.pendingFiles}
            imageUrls={optimistic.imageUrls}
            attachments={optimistic.attachments}
            onRemovePending={optimistic.removePending}
            onRemoveImage={optimistic.removeImage}
            onRemoveAttachment={optimistic.removeAttachment}
          />
          {optimistic.detectedLinks.length > 0 && (
            <LinkPreviewList
              companyId={companyId}
              links={optimistic.detectedLinks}
              onRemove={optimistic.handleRemoveLink}
              onPreviewLoaded={optimistic.handlePreviewLoaded}
            />
          )}
          <div className="flex items-center gap-2">
            <label className="text-xs font-medium text-muted-foreground whitespace-nowrap">
              Target date (optional)
            </label>
            <Input
              type="date"
              value={targetDateInput}
              onChange={e => onTargetDateChange(e.target.value)}
              disabled={creating}
              className="h-8 w-auto text-xs"
            />
          </div>
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-3 text-xs text-muted-foreground dark:border-amber-500/60 dark:bg-amber-500/10">
            <span className="font-semibold text-amber-600 dark:text-amber-400">Tip:</span>{" "}
            Pasted links and dropped files are processed automatically once the project is created. Failed items can be retried individually.
          </div>
        </div>
      </div>
      <div className="mt-4 flex gap-3 shrink-0 flex-col-reverse sm:flex-row sm:items-center sm:justify-between">
        <Button type="button" variant="outline" onClick={onCancel} disabled={creating}>
          Cancel
        </Button>
        <Button
          type="button"
          onClick={() => onSubmit(optimistic.buildPayload())}
          disabled={!canSubmit || creating}
          className="bg-amber-500 text-white hover:bg-amber-500/90 dark:bg-amber-400 dark:hover:bg-amber-400/90"
        >
          {submitLabel}
        </Button>
      </div>
    </>
  );
}

export function PrototypeWalkthroughDialog({open, onOpenChange}: Omit<ModalProps, "onCompleted">) {
  const {companyId} = useCompanyId();
  const [prototypeUrl, setPrototypeUrl] = useState("");
  const [userRequest, setUserRequest] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSubmit = !!prototypeUrl.trim() && !submitting;

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setPrototypeUrl("");
      setUserRequest("");
      setError(null);
    }
    onOpenChange(next);
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await fetch(prototypeWalkthroughAPI(companyId), {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
          prototypeUrl: prototypeUrl.trim(),
          userRequest: userRequest.trim() || undefined,
        }),
      }).then(jr);
      setPrototypeUrl("");
      setUserRequest("");
      onOpenChange(false);
      toast.success("Prototype analysis started. A new project will appear in your workspace once complete.");
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader className="space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-amber-500/10 text-amber-500 dark:bg-amber-500/20">
              <Layout className="h-5 w-5"/>
            </div>
            <div className="space-y-1">
              <DialogTitle>Start from Prototype</DialogTitle>
              <p className="text-xs text-muted-foreground">Analyze a prototype URL and create a project from it.</p>
            </div>
          </div>
          <DialogDescription>
            Provide the URL of a prototype or live app. We&apos;ll analyze the UI, extract screens, and generate a project with features and user stories.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-4 space-y-4">
          {!!error && <div className="mb-2 text-sm text-destructive">{error}</div>}
          <div className="space-y-2">
            <p className="text-sm font-medium">Prototype URL</p>
            <Input
              type="url"
              value={prototypeUrl}
              onChange={e => setPrototypeUrl(e.target.value)}
              placeholder="https://your-prototype-url.com"
              disabled={submitting}
            />
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium">Additional instructions <span className="font-normal text-muted-foreground">(optional)</span></p>
            <ResizableTextarea
              rows={3}
              value={userRequest}
              onChange={e => setUserRequest(e.target.value)}
              placeholder="Any specific areas to focus on, context about the prototype..."
              disabled={submitting}
            />
          </div>
          <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 px-3 py-3 text-xs text-muted-foreground dark:border-amber-500/60 dark:bg-amber-500/10">
            <span className="font-semibold text-amber-600 dark:text-amber-400">Note:</span>{" "}
            The analysis runs in the background. A new project will appear in your workspace once complete.
          </div>
        </div>
        <div className="mt-4 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Button
            type="button"
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className="bg-amber-500 text-white hover:bg-amber-500/90 dark:bg-amber-400 dark:hover:bg-amber-400/90"
          >
            {submitting ? "Starting..." : "Analyze prototype"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
